import { FC } from 'react';

import { ContentStackShareButton } from '@rikstv/play-common/src/components/deepLinkClipboardButton/ShareButton';
import { LinkButton } from '@rikstv/play-common/src/components/linkButton/LinkButton';
import { WishlistButton } from '@rikstv/play-common/src/components/wishlistButton/WishlistButton';
import { commonRoutes } from '@rikstv/play-common/src/router/router.path';
import { authService } from '@rikstv/play-common/src/utils/auth/AuthService';
import { nbsp } from '@rikstv/play-common/src/utils/special-characters/special-characters.utils';
import { useTrailerPlayerStore } from '@rikstv/play-player/src/components/TrailerPlayer/useTrailerPlayerStore';
import { PlayTriangleIcon } from '@rikstv/play-player/src/icons';
import { PrimaryButton, SecondaryButton, SubBody } from '@rikstv/shared-components';

import { AppJumpIcon, CartIcon, ClockIcon, PadlockIcon, TvodIcon, TvSeriesIcon, UserIcon } from '../../../icons';
import { ContentStackMetadata } from '../contentStack.types';
import { getSeriesLinkIfEpisode } from '../hooks/scp.utils';

import { PlayTrailerButton } from './PlayTrailerButton/PlayTrailerButton';
import { RatingButtons } from './RatingButtons/RatingButtons';

import styles from './MediaMetadata.module.css';

export interface MediaMetaProps {
  media: ContentStackMetadata;
  onClickPrimaryAction: (event: React.MouseEvent) => void;
}

const noBreak = (txt: string) => txt.replaceAll(' ', nbsp);

export const MediaButtonList: FC<MediaMetaProps> = ({ media, onClickPrimaryAction }) => {
  const myListHref = media._links.myList?.href;
  const variant = media.defaultProviderVariant;
  const { seriesLink, seriesName } = getSeriesLinkIfEpisode(media);

  return (
    <>
      {variant?.behavior === 'UpgradeSubscription' && (
        <div>
          <br />
          <SubBody>
            <i>Du har dessverre ikke tilgang til dette innholdet i abonnementet ditt.</i>
          </SubBody>
        </div>
      )}
      <div className={styles.buttonList}>
        <PrimaryActionButton media={media} onClickPrimaryAction={onClickPrimaryAction} />
        {seriesLink && (
          <LinkButton buttonStyle="tertiary" to={seriesLink}>
            <TvSeriesIcon style={{ height: '23px' }} />
            <span style={{ paddingLeft: '1ch' }}>Alle episoder</span>
            <span className="sr-only">i {seriesName}</span>
          </LinkButton>
        )}
        <PlayTrailerButton ageLimit={media.metadata.ageLimit} />
        <div className={styles.buttonList}>
          {myListHref != null && (
            <WishlistButton
              assetId={media.tracking.elementId}
              wishlistUrl={myListHref}
              initialState={media.userData?.isInMyList ?? false}
            />
          )}
          <RatingButtons data={media} />
          <ContentStackShareButton title={media.metadata.title} shareHref={media._links.interAppShare?.href} />
        </div>
      </div>
    </>
  );
};

const PrimaryActionButton: FC<MediaMetaProps> = ({ media, onClickPrimaryAction }): React.ReactElement | null => {
  const variant = media.defaultProviderVariant;
  const isPlanned = variant?.state === 'Planned';
  const setPlayBackgroundTrailer = useTrailerPlayerStore(s => s.setPlayBackgroundTrailer);

  // Exhaustive check of behavior's via switch with type guard
  switch (variant?.behavior) {
    case 'SignIn':
      return (
        <>
          <PrimaryButton onClick={onClickPrimaryAction} className={styles.buttonWithIcon}>
            <UserIcon />
            <span>Bli kunde</span>
          </PrimaryButton>
          <SecondaryButton onClick={e => (e.preventDefault(), authService.login())} className={styles.buttonWithIcon}>
            <UserIcon />
            {noBreak(media.primaryButtonText)}
          </SecondaryButton>
        </>
      );
    case 'UpgradeSubscription':
      return (
        <SecondaryButton className={styles.buttonWithIcon} onClick={onClickPrimaryAction}>
          <PadlockIcon />
          {noBreak(media.primaryButtonText)}
        </SecondaryButton>
      );
    case 'ActivateExternalService':
      return (
        <PrimaryButton data-testid="activate-external-service" onClick={onClickPrimaryAction}>
          {noBreak(media.primaryButtonText)}
        </PrimaryButton>
      );
    case 'PlayAsset':
      return (
        <PrimaryButton
          className={styles.buttonWithIcon}
          onClick={e => {
            onClickPrimaryAction(e);
            setPlayBackgroundTrailer(false);
          }}>
          <PlayTriangleIcon />
          {noBreak(media.primaryButtonText)}
        </PrimaryButton>
      );
    case 'PlayChannel':
      return (
        <LinkButton
          onClick={onClickPrimaryAction}
          className={styles.buttonWithIcon}
          to={commonRoutes.watchLive(variant._links.channel.id)}
          buttonStyle="primary">
          <PlayTriangleIcon />
          {noBreak(media.primaryButtonText)}
        </LinkButton>
      );
    case 'PlayExternalAsset':
      return (
        <LinkButton
          data-testid="external-play-btn"
          onClick={onClickPrimaryAction}
          className={styles.buttonWithIcon}
          to={variant._links.externalPlayback.webPlaybackUri}
          buttonStyle="primary">
          <AppJumpIcon iconSize={22} />
          <span>{noBreak(media.primaryButtonText)}</span>
        </LinkButton>
      );
    case 'ShowVariants':
      return (
        <PrimaryButton className={styles.buttonWithIcon} onClick={onClickPrimaryAction}>
          <PlayTriangleIcon />
          {noBreak(media.primaryButtonText)}
        </PrimaryButton>
      );
    case 'RentAsset':
      if (media.type === 'series') {
        //dont display rental button for series page
        return null;
      }

      return (
        <PrimaryButton className={styles.buttonWithIcon} onClick={onClickPrimaryAction}>
          <TvodIcon className={styles.transactionIcons} />
          {noBreak(media.primaryButtonText)}
        </PrimaryButton>
      );
    case 'PurchaseAsset':
      if (media.type === 'series') {
        //dont display purchase button for series page
        return null;
      }

      return (
        <PrimaryButton className={styles.buttonWithIcon} onClick={onClickPrimaryAction}>
          <CartIcon className={styles.transactionIcons} />
          {noBreak(media.primaryButtonText)}
        </PrimaryButton>
      );
    case 'NoAction':
      return (
        <PrimaryButton disabled className={styles.buttonWithIcon}>
          {isPlanned && <ClockIcon />}
          {noBreak(media.primaryButtonText)}
        </PrimaryButton>
      );
    case undefined:
      return null; // must handle undefined for completeness
    default: {
      exhaustiveTypeGuard(variant);
    }
  }
  return null;
};

const exhaustiveTypeGuard = (_: never) => {
  console.error('Unknown behavior', _);
};
